<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useToastStore } from '@/stores/toast'
import { usePermissionsStore } from '@/stores/permissions'
import FosButton from '@/components/ui/FosButton.vue'
import FosSelect from '@/components/ui/FosSelect.vue'

const authStore = useAuthStore()
const toast = useToastStore()
const permissionsStore = usePermissionsStore()

// Stats are agency-level: use agency role directly (no incident/OP context here)
const canViewStats = computed(() => permissionsStore.can('stats.view', authStore.userProfile?.role))

interface IncidentRow {
  incident_id: string
  incident_name: string
  incident_number: string
  status: string
  outcome: string | null
  created_at: string
  closed_at: string
  duration_hours: number | null
  personnel_count: number
  op_count: number
}

const incidents = ref<IncidentRow[]>([])
const loading = ref(false)
const dateFilter = ref<'all' | 'this_year' | 'last_year'>('all')

const dateFilterOptions = [
  { label: 'All time', value: 'all' },
  { label: 'This year', value: 'this_year' },
  { label: 'Last year', value: 'last_year' },
]

const currentYear = new Date().getFullYear()

const filteredIncidents = computed(() => {
  if (dateFilter.value === 'all') return incidents.value
  return incidents.value.filter((row) => {
    if (!row.created_at) return false
    const year = parseInt(row.created_at.slice(0, 4))
    if (dateFilter.value === 'this_year') return year === currentYear
    if (dateFilter.value === 'last_year') return year === currentYear - 1
    return true
  })
})

const displayStats = computed(() => {
  const rows = filteredIncidents.value
  const total = rows.length
  const durations = rows.filter((r) => r.duration_hours !== null).map((r) => r.duration_hours as number)
  const avgDuration = durations.length > 0
    ? Math.round((durations.reduce((a, b) => a + b, 0) / durations.length) * 10) / 10
    : null
  const withOutcome = rows.filter((r) => r.outcome !== null)
  const foundRate = withOutcome.length > 0
    ? Math.round((withOutcome.filter((r) => r.outcome === 'found').length / withOutcome.length) * 1000) / 10
    : null
  return { total, avgDuration, foundRate }
})

async function fetchStats() {
  const agencyId = authStore.userProfile?.agency_id
  if (!agencyId) return
  loading.value = true
  try {
    const token = await authStore.currentUser?.getIdToken()
    const [aggrRes, incRes] = await Promise.all([
      fetch(`/api/agencies/${agencyId}/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`/api/agencies/${agencyId}/stats/incidents`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])

    if (!aggrRes.ok || !incRes.ok) throw new Error('Failed to load statistics')

    const incJson = await incRes.json()
    incidents.value = incJson.data
  } catch (err) {
    toast.add(String(err), 'error')
  } finally {
    loading.value = false
  }
}

async function exportCsv() {
  const agencyId = authStore.userProfile?.agency_id
  if (!agencyId) return
  try {
    const token = await authStore.currentUser?.getIdToken()
    const res = await fetch(`/api/agencies/${agencyId}/stats/incidents/export-csv`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) throw new Error('Export failed')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'incidents.csv'
    a.click()
    URL.revokeObjectURL(url)
  } catch (err) {
    toast.add(String(err), 'error')
  }
}

function outcomeLabel(outcome: string | null): string {
  if (outcome === 'found') return 'Found'
  if (outcome === 'not_found') return 'Not Found'
  if (outcome === 'suspended') return 'Suspended'
  return '—'
}

function outcomeClass(outcome: string | null): string {
  if (outcome === 'found') return 'bg-green-900/40 text-green-400'
  if (outcome === 'not_found') return 'bg-red-900/30 text-error'
  if (outcome === 'suspended') return 'bg-amber-900/40 text-amber-400'
  return 'bg-surface-container-highest text-outline'
}

function statusClass(status: string): string {
  if (status === 'Active') return 'bg-green-900/40 text-green-400'
  if (status === 'Suspended') return 'bg-amber-900/40 text-amber-400'
  return 'bg-surface-container-highest text-outline'
}

onMounted(fetchStats)
</script>

<template>
  <div class="h-full flex flex-col">
    <header class="bg-surface px-6 py-4 flex items-center justify-between border-b border-outline-variant/15 shrink-0">
      <span class="text-on-surface text-lg font-semibold tracking-wide uppercase">Agency Statistics</span>
    </header>

    <div class="flex-1 overflow-y-auto">
    <div v-if="!canViewStats" class="flex flex-col items-center justify-center py-24">
      <span class="material-symbols-outlined text-[32px] text-outline mb-3">lock</span>
      <p class="font-mono text-xs text-outline uppercase tracking-widest">Access restricted</p>
    </div>

    <div v-if="canViewStats" class="max-w-6xl mx-auto px-4 py-8">
      <div v-if="loading" class="flex justify-center py-20">
        <div class="w-8 h-8 border-2 border-outline/30 border-t-outline animate-spin" />
      </div>

      <template v-else>
        <!-- Summary cards -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div class="bg-surface-container p-5">
            <p class="text-3xl font-bold text-on-surface font-mono">{{ displayStats.total }}</p>
            <p class="text-[10px] font-label uppercase tracking-widest text-outline mt-1">Total incidents</p>
          </div>
          <div class="bg-surface-container p-5">
            <p class="text-3xl font-bold text-on-surface font-mono">
              {{ displayStats.avgDuration !== null ? `${displayStats.avgDuration}h` : '—' }}
            </p>
            <p class="text-[10px] font-label uppercase tracking-widest text-outline mt-1">Avg. duration</p>
          </div>
          <div class="bg-surface-container p-5">
            <p class="text-3xl font-bold text-on-surface font-mono">
              {{ displayStats.foundRate !== null ? `${displayStats.foundRate}%` : '—' }}
            </p>
            <p class="text-[10px] font-label uppercase tracking-widest text-outline mt-1">Subject found rate</p>
          </div>
        </div>

        <!-- Toolbar -->
        <div class="flex items-center justify-between mb-4">
          <FosSelect
            v-model="dateFilter"
            :options="dateFilterOptions"
            class="w-40"
          />
          <FosButton variant="secondary" @click="exportCsv">
            <span class="material-symbols-outlined text-[16px]">download</span>
            Export CSV
          </FosButton>
        </div>

        <!-- Per-incident table -->
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-outline-variant/15">
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Name</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Number</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Status</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Outcome</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Created</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Duration</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">Personnel</th>
                <th class="text-left px-3 py-2 text-[10px] font-label uppercase tracking-widest text-outline">OPs</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in filteredIncidents"
                :key="row.incident_id"
                class="border-b border-outline-variant/15 last:border-0 even:bg-surface-container-lowest odd:bg-surface-container-low"
              >
                <td class="px-3 py-2.5 text-on-surface">{{ row.incident_name }}</td>
                <td class="px-3 py-2.5 text-on-surface-variant font-mono text-xs">{{ row.incident_number }}</td>
                <td class="px-3 py-2.5">
                  <span class="font-mono text-xs uppercase tracking-widest px-2 py-0.5" :class="statusClass(row.status)">
                    {{ row.status }}
                  </span>
                </td>
                <td class="px-3 py-2.5">
                  <span
                    v-if="row.outcome"
                    class="font-mono text-xs uppercase tracking-widest px-2 py-0.5"
                    :class="outcomeClass(row.outcome)"
                  >
                    {{ outcomeLabel(row.outcome) }}
                  </span>
                  <span v-else class="text-outline">—</span>
                </td>
                <td class="px-3 py-2.5 text-on-surface-variant text-xs font-mono">{{ row.created_at }}</td>
                <td class="px-3 py-2.5 text-on-surface-variant font-mono text-xs">
                  {{ row.duration_hours !== null ? `${row.duration_hours}h` : '—' }}
                </td>
                <td class="px-3 py-2.5 text-on-surface-variant font-mono text-xs">{{ row.personnel_count }}</td>
                <td class="px-3 py-2.5 text-on-surface-variant font-mono text-xs">{{ row.op_count }}</td>
              </tr>
              <tr v-if="filteredIncidents.length === 0">
                <td colspan="8" class="px-3 py-8 text-center text-outline text-sm">No incidents found.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
    </div>
  </div>
</template>
